import Config from 'react-native-config';
import axios from 'axios';
import dayjs from 'dayjs';
import jsonwebtoken from 'jsonwebtoken';
import { MOBILE_PLATFORM_URL } from 'lib/deepLink/constants';
import { RouteLinkGenerator } from 'store/deepLink/utils';
import { Platform } from 'react-native';
import { JsonObj } from '@mhp/general-interface';
import { OAuth2Response } from './types';

const { TPS_BASE_URL } = Config;

export enum TpsSource {
  FHIR = 'fhir',
  VISIT_PAY = 'visitpay'
}

export type TpsAuthStatus = 'Unauthenticated' | 'Authenticated';

export interface TpsStatus {
  id: TpsSource;
  name: string;
  status: TpsAuthStatus;
  accessToken: string;
  expirationDate: string;
}

interface TPSParams {
  token: string;
  apiKey: string;
  redirect?: string;
  source?: TpsStatus;
  inAppBrowser?: {
    open: (url: string, options?: JsonObj) => Promise<JsonObj>;
    close: () => void;
    warmup: () => Promise<boolean>;
    mayLaunchUrl: (mostLikelyUrl: string, otherUrls: Array<string>) => void;
    openAuth: (url: string, redirectUrl: string, options?: JsonObj) => Promise<JsonObj>;
    closeAuth: () => void;
    isAvailable: () => Promise<boolean>;
  };
}

export interface TPSToken {
  accessToken: string;
  expirationDate: string;
}

const createTPSHeaders = ({ apiKey, token }: TPSParams) => ({
  apiKey,
  Authorization: `Bearer ${token}`
});

const getAuthUrl = async ({ token, apiKey, source, redirect }: TPSParams) => {
  const { id } = source || { id: '' };

  const url = `${TPS_BASE_URL}/auth`;

  return axios(url, {
    headers: createTPSHeaders({ apiKey, token }),
    params: {
      redirectUri: redirect,
      sourceId: id
    }
  }).then(res => res.data.authUrl);
};

const authenticateWithSource = async (params: TPSParams) => {
  const authUrl = await getAuthUrl(params);
  const { inAppBrowser, redirect } = params;

  if (inAppBrowser && (await inAppBrowser.isAvailable()) && redirect) {
    await inAppBrowser.openAuth(authUrl, redirect, {
      forceCloseOnRedirection: false,
      browserPackage: '' // TODO (track & remove): Temporarily solves FireFox bug with latest InAppBrowser 3.5.1 - https://github.com/proyecto26/react-native-inappbrowser/issues/206#issuecomment-713219355
    });

    return true;
  }

  // Handle loading sub auth in the background
  // if (document && Config.USE_TPS_AUTH_REDIRECT !== 'TRUE') {
  //   // Select the tag #authHook
  //   const elem = document.getElementById('authHook');
  //   if (elem && params.source?.id) {
  //     // create a new element
  //     const id = `auth_${params.source?.id}`;
  //     elem.innerHTML += `<iframe id="${id}" src="${authUrl}" width="0" height="0" style="display:none"></iframe>`;
  //     setTimeout(() => {
  //       const iframe = document.getElementById(id);
  //       if (iframe?.remove) {
  //         iframe.remove();
  //       } else if (iframe?.parentNode) {
  //         // Handle IE11
  //         iframe.parentNode.removeChild(iframe);
  //       }
  //     }, 60000); // 1 minute removal
  //     return new Promise(resolve => {
  //       setTimeout(() => {
  //         resolve(true);
  //       }, 5000);
  //     });
  //   }
  // }
  window.location = authUrl;
  return false;
};

export const checkAuthSources = ({ token, apiKey }: TPSParams) => {
  const url = `${TPS_BASE_URL}/client/source`;

  return axios
    .get<TpsStatus[]>(url, {
      headers: createTPSHeaders({ apiKey, token })
    })
    .then(res => res.data);
};

export const authenticateTPS = async (
  { token, apiKey, inAppBrowser }: TPSParams,
  isWeb?: boolean
): Promise<TpsStatus[]> => {
  const sources = await checkAuthSources({ token, apiKey });
  const unauthenticatedSources =
    sources?.filter(source => source.status.toLowerCase() !== 'authenticated') || [];

  if (!unauthenticatedSources.length) {
    return sources;
  }

  const mobileAuthDeeplink = `${MOBILE_PLATFORM_URL}/${RouteLinkGenerator.authCallback().close}`;
  const encodedMobileLink =
    Platform.OS === 'ios' ? encodeURIComponent(mobileAuthDeeplink) : mobileAuthDeeplink;

  const redirect = isWeb ? window?.location?.origin : encodedMobileLink;

  for (const source of sources) {
    if (source.status.toLowerCase() !== 'authenticated') {
      if (source.id === TpsSource.FHIR && isWeb && window?.sessionStorage) {
        window.sessionStorage.setItem('tpsIsRedirecting', 'TRUE');
      }
      // eslint-disable-next-line no-await-in-loop
      await authenticateWithSource({ token, apiKey, source, inAppBrowser, redirect });
    }
  }
  return checkAuthSources({ token, apiKey });
};

export const fetchTPSTokenForSource = async (sourceId: string, { token, apiKey }: TPSParams) => {
  const url = `${TPS_BASE_URL}/service/token`;

  return axios
    .get<TPSToken>(url, {
      headers: createTPSHeaders({ apiKey, token }),
      params: {
        sourceId,
        persist: true
      }
    })
    .then(result => result.data);
};

export const isAuthenticatedWithSource = async ({
  token,
  apiKey,
  source = { id: '' }
}: TPSParams) => {
  const authSources = await checkAuthSources({ token, apiKey });

  for (const authSource of authSources) {
    if (
      authSource.status.toLowerCase() !== 'authenticated' &&
      authSource.id.toLowerCase() === source.id
    ) {
      return false;
    }
  }
  return true;
};

export function formatAuthForStorage(payload: OAuth2Response) {
  return {
    accessToken: payload.access_token,
    accessTokenExpirationDate: dayjs().add(payload.expires_in, 'second'),
    authorizeAdditionalParameters: {},
    tokenAdditionalParameters: {},
    additionalParameters: {},
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    tokenType: payload.token_type,
    scopes: payload.scopes
  };
}

export function safeDecodeJwt(token: string): JsonObj | null {
  try {
    const resultJwt = jsonwebtoken.decode(token, { complete: true });
    return resultJwt;
  } catch (err) {
    return null;
  }
}
